import { ChangeEvent, FC, useState } from "react";
import { useAtomValue } from "jotai";

import { BinStatusEnum, TaskDriverStatusEnum } from "@/domain/models/common";
import { useTranslator } from "@/i18n/useTranslator";

import { useUpdateBinMutation } from "../data/mutations/binMutations";
import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import { useUpdateTaskMutation } from "../data/mutations/taskMutations";
import { useServiceQuery } from "../data/queries/serviceQueries";
import { JobServiceTaskBaseType } from "../domain/task";
import { isDriverReviewingTaskAtom } from "../stores/atoms";
import { DriverScreenWithTextInput } from "./DriverScreenWithTextInput";

const DriverFillingReasonCantDropBin: FC<JobServiceTaskBaseType> = ({
  jobId,
  serviceId,
  taskId,
}) => {
  const { t } = useTranslator();

  const isDriverReviewingTask = useAtomValue(isDriverReviewingTaskAtom);

  const { data: serviceData } = useServiceQuery({ jobId, serviceId });
  const [reasonCantDropBin, setReasonCantDropBin] = useState(
    serviceData?.turnaroundPhoto?.comment ?? "",
  );

  const { mutate: updateBin } = useUpdateBinMutation();

  const {
    mutate: updateService,
    isPending: isUpdatingService,
    variables,
  } = useUpdateServiceMutation();
  const { mutate: updateTask, isPending: isUpdatingTask } =
    useUpdateTaskMutation();

  const onNextClick = () => {
    // If there's a bin attached to the service, remove it and make it available
    if (serviceData?.assignedBin?.id) {
      updateBin({
        id: serviceData.assignedBin?.id,
        status: BinStatusEnum.AVAILABLE,
        currentJob: null,
        currentService: null,
        currentData: null,
      });
    }

    const driverFlowStatus = isDriverReviewingTask
      ? TaskDriverStatusEnum.TASK_REVIEW
      : TaskDriverStatusEnum.TAKING_PHOTO_OBSTRUCTION;

    updateService({
      serviceId,
      jobId,
      turnaroundPhoto: {
        photoUrl: serviceData?.turnaroundPhoto?.photoUrl ?? "",
        comment: reasonCantDropBin,
      },
      driverFlowStatus,
      assignedBin: null,
      assignedBy: null,
      lastUpdatedBy: null,
      stage: null,
      size: null,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus,
    });
  };

  const isLoading = isUpdatingService || isUpdatingTask;

  const isNextButtonLoading =
    isLoading &&
    variables?.driverFlowStatus ===
      TaskDriverStatusEnum.TAKING_PHOTO_OBSTRUCTION;
  const isPreviousButtonLoading =
    isLoading &&
    variables?.driverFlowStatus === TaskDriverStatusEnum.ASKING_CAN_DROP_BIN;

  const goToPreviousStep = () => {
    updateService({
      jobId,
      serviceId,
      driverFlowStatus: TaskDriverStatusEnum.ASKING_CAN_DROP_BIN,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus: TaskDriverStatusEnum.ASKING_CAN_DROP_BIN,
    });
  };

  const handleOnChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setReasonCantDropBin(event.target.value);
  };

  return (
    <DriverScreenWithTextInput
      jobId={jobId}
      serviceId={serviceId}
      taskId={taskId}
      title="driver_filling_cant_drop_bin.title"
      shouldRenderAsTextArea
      inputProps={{
        value: reasonCantDropBin,
        onChange: handleOnChange,
        placeholder: t("driver_filling_cant_drop_bin.placeholder"),
      }}
      bottomButtonProps={{
        isNextButtonLoading,
        nextButtonTitle: isDriverReviewingTask ? "review" : "next",
        onNextButtonClick: onNextClick,
        isNextButtonDisabled: reasonCantDropBin === undefined,
        previousButtonTitle: "driver_filling_cant_drop_bin.back_button",
        onPreviousButtonClick: isDriverReviewingTask
          ? undefined
          : goToPreviousStep,
        isPreviousButtonLoading,
      }}
    />
  );
};

export { DriverFillingReasonCantDropBin };
